<template>
  <slot v-for="entry in entries" name="entry" :entry="entry" />
  <div v-if="status === 'success' && entries.length === 0" class="flex justify-center py-4">
    <slot name="empty">
      <p>{{ emptyText ?? t('noItems') }}</p>
    </slot>
  </div>
  <div v-if="hasNextPage" class="flex justify-center pt-6">
    <CVButton type="button" outline :is-loading="isFetchingNextPage" @click.prevent="fetchNextPage">
      {{ t('loadMore') }}
    </CVButton>
  </div>
</template>

<script lang="ts" setup generic="T">
import { type QueryKey, useInfiniteQuery } from '@tanstack/vue-query';
import type { PaginatedResponse } from '@/entities/pagination/paginated-response';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import CVButton from '@/components/buttons/CVButton.vue';

const { t } = useI18n();

const props = defineProps<{
  load: (page: number) => Promise<PaginatedResponse<T>>;
  queryKey: QueryKey;
  emptyText?: string;
}>();

defineSlots<{
  entry: (props: { entry: T }) => any;
  empty: () => any;
}>();

const { data, status, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
  queryKey: computed(() => props.queryKey),
  queryFn: async ({ pageParam = 1 }) => props.load(pageParam),
  getNextPageParam: (lastPage) => lastPage.meta.nextPage,
  initialPageParam: 1,
});

const entries = useFlattenPaginatedData(data);
</script>
<i18n lang="json">
{
  "en": {
    "loadMore": "Load more",
    "noItems": "No items found"
  },
  "de": {
    "loadMore": "Mehr laden",
    "noItems": "Keine Einträge gefunden"
  },
  "es": {
    "loadMore": "Cargar más",
    "noItems": "No se encontraron elementos"
  }
}
</i18n>
